import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export const FORM_LAYOUT_MAX_COLUMNS = 4;
export const FORM_LAYOUT_HEADING_LEVELS = ['heading', 'subheading'] as const;

const blockId = (what: string) =>
  z
    .string()
    .trim()
    .min(1)
    .max(64)
    .meta({
      description: `The ${what}'s id, chosen by you: 1 to 64 characters, unique in the layout.`,
    });

const blockDisabled = (what: string) =>
  z
    .boolean()
    .optional()
    .meta({
      description: `\`true\` keeps the ${what} in the layout but hides it from customers. Defaults to \`false\`.`,
    });

const DISCOUNT_CODE_ITEM_ID = 'discount-code';

const headingBase = z.object({
  type: z.literal('heading').meta({ description: 'Always `heading`.' }),
  id: blockId('heading'),
  text: z
    .string()
    .trim()
    .min(1, 'Add the heading text')
    .max(200)
    .meta({ description: 'The text shown, up to 200 characters.' }),
  level: z.enum(FORM_LAYOUT_HEADING_LEVELS).meta({
    description: '`heading` for a section title, `subheading` for a smaller one.',
  }),
  disabled: blockDisabled('heading'),
});

const dividerBase = z.object({
  type: z.literal('divider').meta({ description: 'Always `divider`.' }),
  id: blockId('divider'),
  disabled: blockDisabled('divider'),
});

const fieldIdBase = z.object({
  type: z.literal('field').meta({ description: 'Always `field`.' }),
  fieldId: ObjectIdStringValidationSchema.meta({
    description: "The `id` of a field in the page's `fields`.",
  }),
});

const storedDiscountCode = z.object({
  type: z.literal('discount-code').meta({ description: 'Always `discount-code`.' }),
  id: z.string().trim().min(1).max(64).meta({
    description: 'Always `discount-code`: a form has one discount code box at most.',
  }),
});

const DISCOUNT_CODE_DESCRIPTION =
  'Where the discount code box sits. This item only places the box: the box shows while the page has `showCouponCodeField` on. Checkout pages and events only.';

const FIELD_ITEM_DESCRIPTION = 'A field on the page, shown where it sits in the layout.';
const GROUP_DESCRIPTION = 'Items shown side by side in columns.';

function buildGroup<
  Children extends readonly [z.core.$ZodTypeDiscriminable, ...z.core.$ZodTypeDiscriminable[]],
>(children: Children) {
  return z.object({
    type: z.literal('group').meta({ description: 'Always `group`.' }),
    id: blockId('group'),
    name: z.string().trim().max(100).optional().meta({
      description: 'A name for the group, seen only in the dashboard. Up to 100 characters.',
    }),
    columns: z.number().int().min(1).max(FORM_LAYOUT_MAX_COLUMNS).meta({
      description:
        'How many items sit side by side, 1 to 4. On phones the group stacks by default.',
    }),
    items: z.array(z.discriminatedUnion('type', children)).max(200).meta({
      description:
        'What the group holds, filled row by row: fields, headings, dividers and the discount code. Groups never nest.',
    }),
  });
}

function buildLayout<Item extends z.ZodType>(item: Item) {
  return z
    .array(item)
    .max(500)
    .superRefine((layout, ctx) => checkLayoutIds(layout as CheckedLayout, ctx));
}

type CheckedItem = { type: string; fieldId?: string; fieldKey?: string; id?: string };
type CheckedLayout = Array<CheckedItem & { items?: CheckedItem[] }>;

function checkLayoutIds(layout: CheckedLayout, ctx: z.RefinementCtx) {
  const fieldRefs = new Set<string>();
  const blockIds = new Set<string>();
  let discountCodes = 0;
  const visit = (item: CheckedItem, path: (string | number)[]) => {
    if (item.type === 'field') {
      const ref = item.fieldId ?? item.fieldKey;
      if (!ref) return;
      if (fieldRefs.has(ref)) {
        ctx.addIssue({
          code: 'custom',
          path,
          message: 'A field can only appear once in the layout',
        });
      }
      fieldRefs.add(ref);
      return;
    }
    const blockId = item.type === 'discount-code' ? DISCOUNT_CODE_ITEM_ID : item.id;
    if (!blockId) return;
    if (item.type === 'discount-code' && ++discountCodes > 1) {
      ctx.addIssue({ code: 'custom', path, message: 'A form can only have one discount code' });
    }
    if (blockIds.has(blockId)) {
      ctx.addIssue({
        code: 'custom',
        path,
        message: 'Group, heading and divider ids must be unique',
      });
    }
    blockIds.add(blockId);
  };
  layout.forEach((item, index) => {
    visit(item, [index]);
    if (item.type === 'group') {
      (item.items ?? []).forEach((child, childIndex) => visit(child, [index, 'items', childIndex]));
    }
  });
}

const storedGroup = buildGroup([fieldIdBase, headingBase, dividerBase, storedDiscountCode]);
const storedItem = z.discriminatedUnion('type', [
  fieldIdBase,
  headingBase,
  dividerBase,
  storedDiscountCode,
  storedGroup,
]);

const stored = {
  field: fieldIdBase,
  heading: headingBase,
  divider: dividerBase,
  discountCode: storedDiscountCode,
  group: storedGroup,
  item: storedItem,
  layout: buildLayout(storedItem),
};

export const FormLayoutFieldItemSchema = stored.field;
export const FormLayoutHeadingItemSchema = stored.heading;
export const FormLayoutDividerItemSchema = stored.divider;
export const FormLayoutDiscountCodeItemSchema = stored.discountCode;
export const FormLayoutGroupItemSchema = stored.group;
export const FormLayoutItemSchema = stored.item;
export const FormLayoutValidationSchema = stored.layout;

export type FormLayoutFieldItem = z.infer<typeof FormLayoutFieldItemSchema>;
export type FormLayoutHeadingItem = z.infer<typeof FormLayoutHeadingItemSchema>;
export type FormLayoutDividerItem = z.infer<typeof FormLayoutDividerItemSchema>;
export type FormLayoutDiscountCodeItem = z.infer<typeof FormLayoutDiscountCodeItemSchema>;
export type FormLayoutGroupItem = z.infer<typeof FormLayoutGroupItemSchema>;
export type FormLayoutItem = z.infer<typeof FormLayoutItemSchema>;
export type FormLayout = z.infer<typeof FormLayoutValidationSchema>;

export const SaveFormLayoutBodyValidationSchema = z.object({
  formLayout: FormLayoutValidationSchema,
});

const LayoutFieldRecordSchema = z.record(z.string(), z.unknown());

export const FormLayoutResponseValidationSchema = z.object({
  formLayout: FormLayoutValidationSchema,
});

export const DuplicateFieldResponseValidationSchema = z.object({
  field: LayoutFieldRecordSchema,
  formLayout: FormLayoutValidationSchema,
});

export const DuplicateGroupResponseValidationSchema = z.object({
  groupId: z.string(),
  fields: z.array(LayoutFieldRecordSchema),
  formLayout: FormLayoutValidationSchema,
});

// Examples go on the OpenAPI document only: MCP clients read an example item's `id` and `type` as the
// draft-04 schema id keyword.
export const FORM_LAYOUT_EXAMPLE = [
  { type: 'heading', id: 'your-details', text: 'Your details', level: 'heading' },
  {
    type: 'group',
    id: 'name-row',
    name: 'Name',
    columns: 2,
    items: [
      { type: 'field', fieldId: '665f1c2e4a9f3d2b1c0a9e81' },
      { type: 'field', fieldId: '665f1c2e4a9f3d2b1c0a9e82' },
    ],
  },
  { type: 'divider', id: 'divider-1' },
];

export const FORM_LAYOUT_CREATE_EXAMPLE = FORM_LAYOUT_EXAMPLE.map(item =>
  item.type === 'group'
    ? {
        ...item,
        items: [
          { type: 'field', fieldKey: 'first-name' },
          { type: 'field', fieldKey: 'last-name' },
        ],
      }
    : item,
);

export const FORM_LAYOUT_FIELD_ORDER_RULE =
  "`formLayout` on the page decides the form's order. Each field's `order` is its position among the page's fields and follows the layout: saving a layout (in the dashboard or through the API) rewrites every field's `order`. On a page that has never saved a layout, setting `order` moves the field; once a layout is saved, move fields with `formLayout` instead.";

const itemTypesDescription = (fieldReference: string) =>
  `Each item is one of: \`field\` (${fieldReference}), \`heading\` (\`id\`, \`text\`, \`level\` \`heading\` or \`subheading\`), \`divider\` (\`id\`), \`discount-code\` (no other properties) and \`group\` (\`id\`, optional \`name\`, \`columns\` 1 to 4, and \`items\` holding fields, headings, dividers or the discount code; groups never nest). You choose the \`id\` of each heading, divider and group: 1 to 64 characters, unique in the layout.`;

const FORWARD_COMPATIBILITY_DESCRIPTION =
  'More item types may be added later. When reading a layout, skip any item type you do not recognise. When updating, send every item back as you received it, including those.';

const DISCOUNT_CODE_LAYOUT_DESCRIPTION =
  'A `discount-code` item only places the discount code box: the box shows while `showCouponCodeField` is on, and the item does not switch it on. Forms have no discount code, so a form layout must leave it out.';

const FIELD_ORDER_INPUT_DESCRIPTION = `Fields the layout leaves out are added after it, in their current order. ${FORM_LAYOUT_FIELD_ORDER_RULE}`;

const publicHeading = headingBase.meta({
  id: 'FormLayoutHeading',
  description: 'A heading or subheading shown above the items after it.',
});
const publicDivider = dividerBase.meta({
  id: 'FormLayoutDivider',
  description: 'A line that separates parts of the form.',
});
const publicDiscountCode = z
  .object({
    type: z.literal('discount-code').meta({ description: 'Always `discount-code`.' }),
  })
  .meta({ id: 'FormLayoutDiscountCode', description: DISCOUNT_CODE_DESCRIPTION });

const publicField = fieldIdBase.meta({
  id: 'FormLayoutField',
  description: FIELD_ITEM_DESCRIPTION,
});
const publicGroup = buildGroup([
  publicField,
  publicHeading,
  publicDivider,
  publicDiscountCode,
]).meta({ id: 'FormLayoutGroup', description: GROUP_DESCRIPTION });
const publicItem = z
  .discriminatedUnion('type', [
    publicField,
    publicHeading,
    publicDivider,
    publicDiscountCode,
    publicGroup,
  ])
  .meta({ id: 'FormLayoutItem', description: FORWARD_COMPATIBILITY_DESCRIPTION });

const publicFieldInput = z
  .object({
    type: z.literal('field').meta({ description: 'Always `field`.' }),
    fieldKey: z.string().min(1).max(200).meta({
      description:
        'The `key` you gave the field in `fields` in this request. A key that matches no field is rejected.',
    }),
  })
  .meta({ id: 'FormLayoutFieldInput', description: FIELD_ITEM_DESCRIPTION });
const publicGroupInput = buildGroup([
  publicFieldInput,
  publicHeading,
  publicDivider,
  publicDiscountCode,
]).meta({ id: 'FormLayoutGroupInput', description: GROUP_DESCRIPTION });
const publicItemInput = z
  .discriminatedUnion('type', [
    publicFieldInput,
    publicHeading,
    publicDivider,
    publicDiscountCode,
    publicGroupInput,
  ])
  .meta({ id: 'FormLayoutItemInput', description: FORWARD_COMPATIBILITY_DESCRIPTION });

export const PageFormLayoutResponseValidationSchema = z.array(publicItem).meta({
  description: `How the page's form is arranged, top to bottom: fields, headings, dividers, groups of fields shown side by side in columns, and where the discount code box sits. Always returned, even for a page that was never arranged: every field on the page is listed, and \`fieldId\` matches the \`id\` of an entry in \`fields\`. A \`discount-code\` item only places the box, which shows while \`showCouponCodeField\` is on. A placement you saved is returned whether the box is on or off; when none was saved and the box is on, the item is at the end, where the box shows. Forms never have one. ${itemTypesDescription('`fieldId`')} ${FORM_LAYOUT_FIELD_ORDER_RULE} ${FORWARD_COMPATIBILITY_DESCRIPTION}`,
});

export const CreatePageFormLayoutInputValidationSchema = buildLayout(publicItemInput).meta({
  description: `How to arrange the page's form, top to bottom. Reference a field by \`fieldKey\`: the \`key\` you gave it in \`fields\` in this request. ${itemTypesDescription('`fieldKey`')} ${FIELD_ORDER_INPUT_DESCRIPTION} ${DISCOUNT_CODE_LAYOUT_DESCRIPTION} ${FORWARD_COMPATIBILITY_DESCRIPTION}`,
});

export const UpdatePageFormLayoutInputValidationSchema = buildLayout(publicItem).meta({
  description: `Replaces how the page's form is arranged, top to bottom. Reference fields by \`fieldId\`, the ids in the page's \`fields\`; an id that is not on the page is rejected. Send the whole layout: read the current one from \`formLayout\` on the page, change it and send it back. ${itemTypesDescription('`fieldId`')} ${FIELD_ORDER_INPUT_DESCRIPTION} ${DISCOUNT_CODE_LAYOUT_DESCRIPTION} ${FORWARD_COMPATIBILITY_DESCRIPTION}`,
});
